import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import DataTable from '../components/DataTable/DataTable.jsx';
import Pagination from '../components/Pagination/Pagination.jsx';
import checkinsApi from '../services/checkinsApi.js';
import { colors } from '../utils/colors.js';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const PeriodGroup = styled.div`
  display: flex;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  overflow: hidden;
`;

const PeriodButton = styled.button`
  border: none;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${(props) => (props.$active ? colors.gradients.primary : colors.white)};
  color: ${(props) => (props.$active ? colors.white : colors.gray[700])};

  & + & {
    border-left: 1px solid ${colors.gray[300]};
  }
`;

const columns = [
  { key: 'space_nome', label: 'Space' },
  {
    key: 'date',
    label: 'Data',
    render: (row) => {
      const [year, month, day] = row.date.split('-');
      return `${day}/${month}/${year}`;
    },
  },
  { key: 'total', label: 'Total de Checkins' },
];

function toDateStr(date) {
  return date.toISOString().slice(0, 10);
}

function periodoDiaAnterior() {
  const ontem = new Date();
  ontem.setDate(ontem.getDate() - 1);
  const str = toDateStr(ontem);
  return { data_inicio: str, data_fim: str };
}

function periodoSemana() {
  const hoje = new Date();
  const inicio = new Date();
  inicio.setDate(hoje.getDate() - 6);
  return { data_inicio: toDateStr(inicio), data_fim: toDateStr(hoje) };
}

function periodoMes() {
  const hoje = new Date();
  const inicio = new Date();
  inicio.setDate(hoje.getDate() - 29);
  return { data_inicio: toDateStr(inicio), data_fim: toDateStr(hoje) };
}

const PERIODOS = {
  dia: { label: 'Dia anterior', compute: periodoDiaAnterior },
  semana: { label: 'Semana', compute: periodoSemana },
  mes: { label: 'Mês', compute: periodoMes },
};

export default function Checkins() {
  const [periodo, setPeriodo] = useState('mes');
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const load = useCallback(() => {
    setLoading(true);
    const { data_inicio, data_fim } = PERIODOS[periodo].compute();
    checkinsApi
      .listPorSpace({ data_inicio, data_fim, page, per_page: perPage })
      .then((data) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [periodo, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePeriodoChange = (value) => {
    setPeriodo(value);
    setPage(1);
  };

  return (
    <div>
      <Header>
        <Title>Checkins por Space</Title>
        <PeriodGroup>
          {Object.entries(PERIODOS).map(([key, { label }]) => (
            <PeriodButton key={key} type="button" $active={periodo === key} onClick={() => handlePeriodoChange(key)}>
              {label}
            </PeriodButton>
          ))}
        </PeriodGroup>
      </Header>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        emptyMessage="Nenhum checkin encontrado no período selecionado."
      />
      {!loading && (
        <Pagination
          page={page}
          perPage={perPage}
          total={total}
          onPageChange={setPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}
